import { useState } from "react";
import { motion, AnimatePresence } from "motion/react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { questions } from "../data/quiz-data";
import { SoftProgressBar } from "../components/SoftProgressBar";
import { AnswerOption } from "../components/AnswerOption";
import Analytics from "../../../utils/analytics";

interface QuizScreenProps {
  onNavigate: (nextScreen: string, score?: number) => void;
}

export function Quiz({ onNavigate }: QuizScreenProps) {
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [direction, setDirection] = useState(1);

  const question = questions[currentQuestion];
  const isAnswered = answers[question.id] !== undefined;
  const isLastQuestion = currentQuestion === questions.length - 1;

  const handleAnswer = (value: number) => {
    setAnswers((prev) => ({ ...prev, [question.id]: value }));
    //console.log(`Answered Question ${question.id} with value ${value}`);
    Analytics.getInstance().trackBalanceTest("q" + question.id, value.toString());
  };

  const handleNext = () => {
    if (!isAnswered) return;

    if (isLastQuestion) {
      const totalScore = Object.values(answers).reduce((sum, val) => sum + val, 0);
      //console.log("Total Score:", totalScore);
      onNavigate("result", totalScore);
    } else {
      setDirection(1);
      setCurrentQuestion((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentQuestion > 0) {
      setDirection(-1);
      setCurrentQuestion((prev) => prev - 1);
    }
  };

  return (
    <div className="pt-6 pb-6 flex items-center justify-center p-4 bg-gradient-to-br background-sand">
      <div className="max-w-2xl w-full">
        <div className="mb-8">
          <SoftProgressBar currentStep={currentQuestion + 1} totalSteps={questions.length} />
        </div>

        <AnimatePresence mode="wait" custom={direction}>
          <motion.div
            key={currentQuestion}
            custom={direction}
            initial={{ opacity: 0, x: direction * 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: direction * -50 }}
            transition={{ duration: 0.3 }}
            className="bg-white rounded-3xl p-8 shadow-xl"
          >
            <div className="mb-2 text-sm opacity-60">{question.microcopy}</div>
            <h2 className="mb-8 text-2xl leading-relaxed">{question.text}</h2>

            <div className="space-y-3 mb-8">
              {question.options.map((option, index) => (
                <AnswerOption
                  key={index}
                  text={option.text}
                  isSelected={answers[question.id] === option.value}
                  onClick={() => handleAnswer(option.value)}
                />
              ))}
            </div>

            <div className="flex items-center justify-between gap-4">
              <button
                onClick={handleBack}
                disabled={currentQuestion === 0}
                className="px-6 py-3 rounded-full flex items-center gap-2 transition-opacity disabled:opacity-30 hover:opacity-70 cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5" />
                Tilbage
              </button>

              <motion.button
                onClick={handleNext}
                disabled={!isAnswered}
                whileHover={isAnswered ? { scale: 1.02 } : {}}
                whileTap={isAnswered ? { scale: 0.98 } : {}}
                className={`px-8 py-4 rounded-full flex items-center gap-2 transition-all ${
                  isAnswered
                    ? "button text-white shadow-lg hover:shadow-xl cursor-pointer"
                    : "button tailwind-button text-gray-400 cursor-not-allowed"
                }`}
              >
                {isLastQuestion ? "Se dit resultat" : "Næste"}
                <ArrowRight className="w-5 h-5" />
              </motion.button>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}